import React, { useCallback, useEffect, useState } from 'react';
import { usePage } from '../../hooks/usePage';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import {
  approvalsApi,
  ApprovalRequest,
  ApprovalStatus,
  ListApprovalsResult,
} from '../../api/approvals';
import { usePendingApprovals } from '../../contexts/PendingApprovalsContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Table } from '../../components/ui/Table';
import { Drawer } from '../../components/ui/Drawer';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';

const PAGE_SIZE = 25;

const STATUS_OPTIONS: { value: ApprovalStatus | ''; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'executed', label: 'Executed' },
  { value: 'failed', label: 'Failed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'expired', label: 'Expired' },
  { value: '', label: 'All' },
];

const STATUS_VARIANTS: Record<
  ApprovalStatus,
  'success' | 'danger' | 'warning' | 'info' | 'neutral'
> = {
  pending: 'warning',
  executed: 'success',
  failed: 'danger',
  rejected: 'danger',
  cancelled: 'neutral',
  expired: 'neutral',
};

const formatDateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-NG', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const titleCase = (s: string) =>
  s.replace(/[_.]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

/** "in 3h", "in 2d", "expired" — checkers triage by urgency. */
const expiresIn = (iso: string) => {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const hours = Math.round(ms / 3_600_000);
  if (hours < 1) return 'in <1h';
  if (hours < 48) return `in ${hours}h`;
  return `in ${Math.round(hours / 24)}d`;
};

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '140px 1fr',
      gap: 12,
      padding: '10px 0',
      borderBottom: '1px solid var(--neutral-200)',
    }}
  >
    <span style={{ fontSize: 12, color: 'var(--neutral-500)', fontWeight: 500 }}>
      {label}
    </span>
    <div style={{ fontSize: 13, color: 'var(--neutral-800)', wordBreak: 'break-word' }}>
      {children}
    </div>
  </div>
);

export const Approvals: React.FC = () => {
  usePage('Approvals');
  const toast = useToast();
  const { admin, can } = useAuth();
  const { refresh: refreshPendingCount } = usePendingApprovals();

  const [status, setStatus] = useState<ApprovalStatus | ''>('pending');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ListApprovalsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ApprovalRequest | null>(null);
  const [confirming, setConfirming] = useState<'approve' | 'reject' | null>(null);
  const [reviewBusy, setReviewBusy] = useState(false);

  const canView = can('approvals.view');
  const canReview = can('approvals.review');

  const fetchApprovals = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const result = await approvalsApi.list({
        status: status || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setData(result);
    } catch (err) {
      toast.error(err, 'Failed to load approval requests.');
    } finally {
      setLoading(false);
    }
  }, [canView, status, page, toast]);

  useEffect(() => {
    setPage(1);
  }, [status]);

  useEffect(() => {
    void fetchApprovals();
  }, [fetchApprovals]);

  const afterReview = async () => {
    setConfirming(null);
    setSelected(null);
    await Promise.all([fetchApprovals(), refreshPendingCount()]);
  };

  const handleApprove = async () => {
    if (!selected) return;
    try {
      setReviewBusy(true);
      await approvalsApi.approve(selected.id);
      toast.success('Approved — the action has been executed.');
      await afterReview();
    } catch (err) {
      // 403 = self-approval blocked (maker-checker); the backend message
      // ("you cannot approve your own request") is surfaced as-is.
      toast.error(err, 'Failed to approve this request.');
      await afterReview();
    } finally {
      setReviewBusy(false);
    }
  };

  const handleReject = async (note?: string) => {
    if (!selected) return;
    const isOwn = isOwnRequest(selected);
    try {
      setReviewBusy(true);
      await approvalsApi.reject(selected.id, note);
      toast.success(isOwn ? 'Request cancelled.' : 'Request rejected.');
      await afterReview();
    } catch (err) {
      toast.error(err, 'Failed to reject this request.');
    } finally {
      setReviewBusy(false);
    }
  };

  const isOwnRequest = (approval: ApprovalRequest) =>
    !!admin && approval.requestedById === admin.id;

  if (!canView) {
    return (
      <Card padding={48}>
        <p style={{ textAlign: 'center', color: 'var(--neutral-500)', fontSize: 14 }}>
          You don't have permission to view approval requests.
        </p>
      </Card>
    );
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const selectedIsOwn = selected ? isOwnRequest(selected) : false;

  return (
    <>
      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
            Fund-moving admin actions wait here until a second admin approves
            them. Approving executes the action immediately.
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {STATUS_OPTIONS.map((opt) => {
              const active = status === opt.value;
              return (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setStatus(opt.value)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 'var(--radius-full)',
                    border: `1px solid ${active ? 'var(--blue-600)' : 'var(--neutral-300)'}`,
                    background: active ? 'var(--blue-100)' : 'var(--neutral-100)',
                    color: active ? 'var(--blue-700)' : 'var(--neutral-600)',
                    fontSize: 13,
                    fontWeight: active ? 600 : 500,
                    cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : data ? (
          <>
            <Table<ApprovalRequest>
              data={data.items}
              onRowClick={setSelected}
              emptyMessage={
                status === 'pending'
                  ? 'No requests are waiting for review.'
                  : 'No approval requests match this filter.'
              }
              columns={[
                {
                  key: 'summary',
                  header: 'Action',
                  render: (a) => (
                    <div>
                      <div style={{ fontWeight: 600 }}>{a.summary}</div>
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--neutral-500)',
                          fontFamily: 'monospace',
                        }}
                      >
                        {a.actionKey}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'requestedBy',
                  header: 'Requested by',
                  render: (a) => (
                    <span style={{ fontSize: 13 }}>
                      {a.requestedByEmail ?? '—'}
                      {isOwnRequest(a) ? (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: 11,
                            color: 'var(--blue-700)',
                            fontWeight: 600,
                          }}
                        >
                          (you)
                        </span>
                      ) : null}
                    </span>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (a) => (
                    <Badge variant={STATUS_VARIANTS[a.status]}>
                      {titleCase(a.status)}
                    </Badge>
                  ),
                },
                {
                  key: 'createdAt',
                  header: 'Requested',
                  render: (a) => (
                    <span style={{ whiteSpace: 'nowrap', color: 'var(--neutral-600)' }}>
                      {formatDateTime(a.createdAt)}
                    </span>
                  ),
                },
                {
                  key: 'expiresAt',
                  header: 'Expires',
                  render: (a) =>
                    a.status === 'pending' ? (
                      <span style={{ whiteSpace: 'nowrap', color: 'var(--neutral-600)' }}>
                        {expiresIn(a.expiresAt)}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--neutral-400)' }}>—</span>
                    ),
                },
              ]}
            />
            <Pagination
              page={data.page}
              totalPages={totalPages}
              total={data.total}
              onPageChange={setPage}
            />
          </>
        ) : null}
      </Card>

      <Drawer
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.summary ?? ''}
        subtitle={selected ? titleCase(selected.status) : ''}
        width={560}
      >
        {selected && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <Badge variant={STATUS_VARIANTS[selected.status]}>
                {titleCase(selected.status)}
              </Badge>
            </div>

            <InfoRow label="Action key">
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {selected.actionKey}
              </span>
            </InfoRow>
            <InfoRow label="Target">
              {selected.targetType ? (
                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {selected.targetType} · {selected.targetId}
                </span>
              ) : (
                '—'
              )}
            </InfoRow>
            <InfoRow label="Requested by">
              {selected.requestedByEmail ?? '—'}
              {selectedIsOwn ? ' (you)' : ''}
            </InfoRow>
            <InfoRow label="Requested at">{formatDateTime(selected.createdAt)}</InfoRow>
            {selected.status === 'pending' ? (
              <InfoRow label="Expires">
                {formatDateTime(selected.expiresAt)} ({expiresIn(selected.expiresAt)})
              </InfoRow>
            ) : (
              <>
                <InfoRow label="Reviewed by">{selected.reviewedByEmail ?? '—'}</InfoRow>
                <InfoRow label="Reviewed at">{formatDateTime(selected.reviewedAt)}</InfoRow>
                {selected.reviewNote ? (
                  <InfoRow label="Review note">{selected.reviewNote}</InfoRow>
                ) : null}
              </>
            )}

            {selected.failureReason ? (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: 'var(--danger-100)',
                  color: 'var(--danger-900)',
                  fontSize: 13,
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--danger-300)',
                }}
              >
                Approved, but execution failed — the action did NOT happen:{' '}
                {selected.failureReason}
              </div>
            ) : null}

            {selected.status === 'pending' && canReview ? (
              <div style={{ marginTop: 24 }}>
                {selectedIsOwn ? (
                  <p
                    style={{
                      fontSize: 13,
                      color: 'var(--neutral-500)',
                      marginBottom: 12,
                    }}
                  >
                    You requested this action, so you can't approve it — a
                    second admin must. You can cancel it.
                  </p>
                ) : null}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button
                    variant="secondary"
                    onClick={() => setConfirming('reject')}
                    disabled={reviewBusy}
                  >
                    {selectedIsOwn ? 'Cancel request' : 'Reject'}
                  </Button>
                  {!selectedIsOwn && (
                    <Button onClick={() => setConfirming('approve')} disabled={reviewBusy}>
                      Approve & execute
                    </Button>
                  )}
                </div>
              </div>
            ) : null}
          </>
        )}
      </Drawer>

      <ConfirmDialog
        isOpen={confirming === 'approve'}
        title="Approve & execute"
        message={
          selected
            ? `This immediately executes: "${selected.summary}". This moves funds and cannot be undone.`
            : ''
        }
        confirmLabel="Approve & execute"
        variant="danger"
        loading={reviewBusy}
        onConfirm={() => void handleApprove()}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        isOpen={confirming === 'reject'}
        title={selectedIsOwn ? 'Cancel this request' : 'Reject this request'}
        message={
          selected
            ? `"${selected.summary}" will not be executed. The maker can re-initiate it later.`
            : ''
        }
        confirmLabel={selectedIsOwn ? 'Cancel request' : 'Reject'}
        variant="danger"
        requireReason
        reasonLabel="Note"
        reasonPlaceholder="Why is this being rejected?"
        loading={reviewBusy}
        onConfirm={(note) => void handleReject(note)}
        onCancel={() => setConfirming(null)}
      />
    </>
  );
};
